/**
 * Template de prise de besoin : Post-Incident Review (PIR) v1
 * Aligné sur ITIL 4, COBIT 2019, ISO 27001 et NIST CSF 2.0
 * Document d'analyse post-incident
 */

import type { IntakeTemplate } from '../templateTypes';

export const templatePostIncidentReviewV1: IntakeTemplate = {
  id: 'post-incident-review-v1',
  name: 'Prise de besoin Post-Incident Review (PIR) v1',
  description:
    'Questionnaire complet pour élaborer un Post-Incident Review conforme ITIL 4, COBIT 2019, ISO 27001 et NIST CSF 2.0. Définit l\'analyse des causes, la réponse, les impacts et les leçons apprises d\'un incident.',
  documentTypeCode: 'POST_INCIDENT_REVIEW',
  version: '1.0.0',
  phases: [
    {
      id: 'phase1',
      title: 'Contexte & Identification',
      description: 'Informations sur l\'incident et l\'entreprise',
      icon: 'Building2',
      questions: [
        {
          id: 'entreprise',
          label: 'Entreprise',
          type: 'text',
          placeholder: 'Nom de l\'entreprise',
          required: true,
          helpText: 'Identifiant principal pour le rapport.',
        },
        {
          id: 'nom_incident',
          label: 'Nom de l\'incident',
          type: 'text',
          placeholder: 'Ex: Incident de sécurité - Phishing massif',
          required: true,
          helpText: 'Nom descriptif de l\'incident pour identification.',
        },
        {
          id: 'date_incident',
          label: 'Date de l\'incident',
          type: 'date',
          required: true,
          helpText: 'Date de survenue de l\'incident.',
        },
        {
          id: 'type_incident',
          label: 'Type d\'incident',
          type: 'select',
          options: [
            'Incident de sécurité',
            'Incident de disponibilité',
            'Incident d\'intégrité',
            'Incident de confidentialité',
            'Incident majeur',
            'Autre',
          ],
          required: true,
          helpText: 'ITIL 4: Classification du type d\'incident.',
        },
        {
          id: 'severite_incident',
          label: 'Sévérité de l\'incident',
          type: 'select',
          options: ['P1 / CAT 1', 'P2 / CAT 2', 'P3 / CAT 3', 'P4 / CAT 4', 'P5 / CAT 5'],
          required: true,
          helpText: 'ITIL 4 / NIST CSF 2.0: Sévérité selon classification.',
        },
        {
          id: 'ton_document',
          label: 'Quel ton souhaitez-vous pour ce rapport ?',
          type: 'select',
          options: [
            'Formel et analytique (ton professionnel, analyse approfondie)',
            'Pédagogique et explicatif (ton guide pratique)',
            'Mixte (équilibre entre formel et pédagogique)',
          ],
          helpText: 'Le ton influence la facilité de compréhension du rapport.',
        },
      ],
    },
    {
      id: 'phase2',
      title: 'Chronologie & Détection',
      description: 'Chronologie de l\'incident et processus de détection',
      icon: 'Clock',
      questions: [
        {
          id: 'chronologie_incident',
          label: 'Chronologie détaillée de l\'incident',
          type: 'textarea',
          placeholder: 'Décrivez les événements dans l\'ordre chronologique: Détection → Analyse → Réponse → Résolution...',
          required: true,
          helpText: 'ITIL 4: Chronologie complète pour compréhension de l\'incident.',
        },
        {
          id: 'methode_detection',
          label: 'Comment l\'incident a-t-il été détecté ?',
          type: 'select',
          options: [
            'Détection automatique (SIEM, antivirus, firewall)',
            'Rapport utilisateur',
            'Surveillance proactive',
            'Alerte externe',
            'Audit de sécurité',
            'Autre',
          ],
          required: true,
          helpText: 'NIST CSF 2.0 (DE.CM): Méthode de détection pour amélioration.',
        },
        {
          id: 'temps_detection',
          label: 'Temps écoulé avant détection',
          type: 'text',
          placeholder: 'Ex: 2 heures, 1 jour, 3 jours...',
          helpText: 'MTTD (Mean Time To Detect) pour analyse de performance.',
        },
        {
          id: 'premiers_indices',
          label: 'Quels étaient les premiers indices de l\'incident ?',
          type: 'textarea',
          placeholder: 'Décrivez les signaux d\'alerte initiaux...',
          helpText: 'ITIL 4: Indices pour améliorer la détection précoce.',
        },
      ],
    },
    {
      id: 'phase3',
      title: 'Analyse & Impact',
      description: 'Analyse de cause racine et impact',
      icon: 'Target',
      questions: [
        {
          id: 'cause_racine',
          label: 'Quelle est la cause racine identifiée ?',
          type: 'textarea',
          placeholder: 'Décrivez la cause racine de l\'incident...',
          required: true,
          helpText: 'ITIL 4: Cause racine pour prévention des récurrences.',
        },
        {
          id: 'facteurs_contributifs',
          label: 'Quels facteurs ont contribué à l\'incident ?',
          type: 'multiselect',
          options: [
            'Erreur humaine',
            'Mauvaise configuration',
            'Vulnérabilité logicielle',
            'Manque de formation',
            'Processus inadéquat',
            'Ressources insuffisantes',
            'Manque de surveillance',
            'Autre',
          ],
          helpText: 'ITIL 4: Facteurs contributifs pour analyse complète.',
        },
        {
          id: 'impact_business',
          label: 'Quel a été l\'impact business de l\'incident ?',
          type: 'textarea',
          placeholder: 'Décrivez l\'impact: interruption de service, perte de données, impact financier, réputation...',
          required: true,
          helpText: 'ITIL 4: Impact business pour évaluation des risques.',
        },
        {
          id: 'utilisateurs_affectes',
          label: 'Combien d\'utilisateurs ont été affectés ?',
          type: 'number',
          placeholder: 'Nombre d\'utilisateurs',
          helpText: 'ITIL 4: Nombre d\'utilisateurs pour évaluation de l\'impact.',
        },
        {
          id: 'duree_interruption',
          label: 'Durée de l\'interruption de service',
          type: 'text',
          placeholder: 'Ex: 2 heures, 1 jour...',
          helpText: 'ITIL 4: Durée pour évaluation de l\'impact.',
        },
      ],
    },
    {
      id: 'phase4',
      title: 'Réponse & Résolution',
      description: 'Actions de réponse et processus de résolution',
      icon: 'CheckCircle',
      questions: [
        {
          id: 'actions_reponse',
          label: 'Quelles actions ont été prises pour répondre à l\'incident ?',
          type: 'textarea',
          placeholder: 'Décrivez les actions: confinement, éradication, restauration...',
          required: true,
          helpText: 'NIST CSF 2.0 (RS.MI): Actions de réponse pour évaluation.',
        },
        {
          id: 'temps_resolution',
          label: 'Temps total de résolution',
          type: 'text',
          placeholder: 'Ex: 4 heures, 1 jour, 3 jours...',
          helpText: 'MTTR (Mean Time To Resolve) pour analyse de performance.',
        },
        {
          id: 'equipe_intervention',
          label: 'Qui a participé à la résolution ?',
          type: 'textarea',
          placeholder: 'Décrivez l\'équipe: rôles, responsabilités...',
          helpText: 'ITIL 4: Équipe pour évaluation de la collaboration.',
        },
        {
          id: 'difficultes_rencontrees',
          label: 'Quelles difficultés ont été rencontrées lors de la résolution ?',
          type: 'textarea',
          placeholder: 'Décrivez les difficultés: manque d\'outils, manque d\'information, coordination...',
          helpText: 'ITIL 4: Difficultés pour identification des améliorations.',
        },
      ],
    },
    {
      id: 'phase5',
      title: 'Leçons Apprises & Amélioration',
      description: 'Leçons apprises et plan d\'amélioration',
      icon: 'TrendingUp',
      questions: [
        {
          id: 'lecons_apprises',
          label: 'Quelles sont les principales leçons apprises ?',
          type: 'textarea',
          placeholder: 'Décrivez les leçons: ce qui a bien fonctionné, ce qui doit être amélioré...',
          required: true,
          helpText: 'ITIL 4: Leçons apprises pour amélioration continue.',
        },
        {
          id: 'bonnes_pratiques',
          label: 'Quelles bonnes pratiques ont été identifiées ?',
          type: 'textarea',
          placeholder: 'Décrivez les bonnes pratiques à reproduire...',
          helpText: 'ITIL 4: Bonnes pratiques pour standardisation.',
        },
        {
          id: 'ameliorations_recommandees',
          label: 'Quelles améliorations sont recommandées ?',
          type: 'textarea',
          placeholder: 'Décrivez les améliorations: processus, outils, formation, documentation...',
          required: true,
          helpText: 'ITIL 4: Améliorations pour plan d\'action.',
        },
        {
          id: 'actions_correctives',
          label: 'Quelles actions correctives doivent être mises en place ?',
          type: 'textarea',
          placeholder: 'Décrivez les actions: corrections immédiates, préventions, formations...',
          helpText: 'ITIL 4: Actions correctives pour prévention des récurrences.',
        },
        {
          id: 'suivi_ameliorations',
          label: 'Comment sera suivi le plan d\'amélioration ?',
          type: 'textarea',
          placeholder: 'Décrivez le suivi: responsables, délais, indicateurs...',
          helpText: 'ITIL 4: Suivi pour garantir l\'implémentation des améliorations.',
        },
      ],
    },
  ],
};





